import { RefObject, useEffect, useLayoutEffect, useRef } from "react";

type RouteLocation = {
  pathname: string;
  search: string;
  key?: string;
};

type ScrollPosition = {
  top: number;
  left: number;
  path: number[];
};

const scrollPositions = new Map<string, ScrollPosition>();
const scrollableOverflow = /auto|scroll|overlay/;

const routeKey = (location: RouteLocation) =>
  `${location.pathname}${location.search}`;

export const isScrollableElement = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  const canScrollY =
    scrollableOverflow.test(style.overflowY) &&
    element.scrollHeight > element.clientHeight + 1;
  const canScrollX =
    scrollableOverflow.test(style.overflowX) &&
    element.scrollWidth > element.clientWidth + 1;

  return canScrollY || canScrollX;
};

export const getScrollElementPath = (
  root: HTMLElement,
  target: HTMLElement
) => {
  if (!root.contains(target)) return null;
  if (root === target) return [];

  const path: number[] = [];
  let current: HTMLElement | null = target;

  while (current && current !== root) {
    const parent = current.parentElement;
    if (!parent) return null;

    const index = Array.prototype.indexOf.call(parent.children, current);
    if (index === -1) return null;

    path.unshift(index);
    current = parent;
  }

  return path;
};

export const getElementByPath = (root: HTMLElement, path: number[]) => {
  let current: HTMLElement = root;

  for (const index of path) {
    const child = current.children.item(index);
    if (!(child instanceof HTMLElement)) return null;
    current = child;
  }

  return current;
};

export const findScrollableElement = (root: HTMLElement) => {
  if (isScrollableElement(root)) return root;

  return (
    Array.from(root.querySelectorAll<HTMLElement>("*")).find(
      isScrollableElement
    ) || root
  );
};

const restoreScroll = (root: HTMLElement, position: ScrollPosition) => {
  const savedTarget = getElementByPath(root, position.path);
  const target =
    savedTarget && isScrollableElement(savedTarget)
      ? savedTarget
      : findScrollableElement(root);

  target.scrollTop = position.top;
  target.scrollLeft = position.left;
};

export function useRouteScrollRestoration(
  location: RouteLocation,
  navigationType: string,
  rootRef: RefObject<HTMLElement>
) {
  const currentKeyRef = useRef(routeKey(location));

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    currentKeyRef.current = routeKey(location);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const handleScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!root.contains(target) || !isScrollableElement(target)) return;

      const path = getScrollElementPath(root, target);
      if (!path) return;

      scrollPositions.set(currentKeyRef.current, {
        top: target.scrollTop,
        left: target.scrollLeft,
        path,
      });
    };

    root.addEventListener("scroll", handleScroll, true);
    return () => root.removeEventListener("scroll", handleScroll, true);
  }, [rootRef]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const savedPosition = scrollPositions.get(routeKey(location));
    if (navigationType !== "POP" || !savedPosition) {
      findScrollableElement(root).scrollTop = 0;
      return undefined;
    }

    const timeouts = [100, 350, 800].map((delay) =>
      window.setTimeout(() => restoreScroll(root, savedPosition), delay)
    );
    const frame = window.requestAnimationFrame(() =>
      restoreScroll(root, savedPosition)
    );

    return () => {
      window.cancelAnimationFrame(frame);
      timeouts.forEach(window.clearTimeout);
    };
  }, [location.key, location.pathname, location.search, navigationType, rootRef]);
}
